import type { InboundVideoStats, OutboundVideoStats, RtcStatsSnapshot } from './types'
import {
  formatPacketLossPercent,
  getInboundQualityLabel,
  getInboundQualityScore,
  getResolutionQualityLabel,
} from './video-quality-labels'

type RtcStat = Record<string, unknown>

const VIDEO_KIND = 'video'

const calcBitrateKbps = (
  bytesSent: number,
  timestamp: number,
  prev?: RtcStatsSnapshot,
): number | undefined => {
  if (!prev?.bytesSent) return undefined

  const deltaBytes = bytesSent - prev.bytesSent
  const deltaTime = (timestamp - prev.timestamp) / 1000

  if (deltaTime <= 0 || deltaBytes < 0) return undefined

  return (deltaBytes * 8) / deltaTime / 1000
}

const calcPacketLossPercent = (
  packetsLost: number,
  packetsReceived: number,
  prev?: RtcStatsSnapshot,
): number => {
  if (!prev?.packetsLost && prev?.packetsLost !== 0) return 0

  const deltaLost = packetsLost - (prev.packetsLost ?? 0)
  const deltaReceived = packetsReceived - (prev.packetsReceived ?? 0)
  const total = deltaLost + deltaReceived

  if (total <= 0) return 0

  return (deltaLost / total) * 100
}

const isVideoRtpStat = (stat: RtcStat, direction: 'inbound' | 'outbound'): boolean => {
  if (stat.type !== `${direction}-rtp`) return false
  return stat.kind === VIDEO_KIND || stat.mediaType === VIDEO_KIND
}

const getTrackIdentifier = (stat: RtcStat): string | undefined => {
  const trackId = stat.trackId ?? stat.trackIdentifier
  return typeof trackId === 'string' ? trackId : undefined
}

export const resolveMediaTrackIdFromReport = (
  report: RTCStatsReport,
  rtpStat: RtcStat,
): string | undefined => {
  const refId = getTrackIdentifier(rtpStat)
  if (!refId) return undefined

  let resolved: string | undefined

  report.forEach((stat) => {
    const trackStat = stat as RtcStat
    if (trackStat.id === refId && trackStat.type === 'track') {
      const trackIdentifier = trackStat.trackIdentifier
      if (typeof trackIdentifier === 'string') {
        resolved = trackIdentifier
      }
    }
  })

  return resolved ?? refId
}

const collectStats = (report: RTCStatsReport): RtcStat[] => {
  const stats: RtcStat[] = []
  report.forEach((stat) => {
    stats.push(stat as RtcStat)
  })
  return stats
}

export const parseOutboundVideoStats = (
  report: RTCStatsReport,
  prevSnapshot?: RtcStatsSnapshot,
  trackId?: string,
): OutboundVideoStats | null => {
  const stats = collectStats(report)
  const outboundRtpStats = stats.filter((stat) => isVideoRtpStat(stat, 'outbound'))

  const rtpStat = trackId
    ? outboundRtpStats.find((stat) => getTrackIdentifier(stat) === trackId)
    : outboundRtpStats.at(0)

  if (!rtpStat) return null

  const bytesSent = typeof rtpStat.bytesSent === 'number' ? rtpStat.bytesSent : undefined
  const timestamp = typeof rtpStat.timestamp === 'number' ? rtpStat.timestamp : undefined
  const width = typeof rtpStat.frameWidth === 'number' ? rtpStat.frameWidth : 0
  const height = typeof rtpStat.frameHeight === 'number' ? rtpStat.frameHeight : 0

  if (typeof bytesSent !== 'number' || typeof timestamp !== 'number') return null

  const bitrateKbps = calcBitrateKbps(bytesSent, timestamp, prevSnapshot) ?? 0
  const fps = typeof rtpStat.framesPerSecond === 'number' ? rtpStat.framesPerSecond : undefined
  const limitationReason = typeof rtpStat.qualityLimitationReason === 'string'
    ? rtpStat.qualityLimitationReason as OutboundVideoStats['limitationReason']
    : undefined

  const result: OutboundVideoStats = {
    bitrateKbps,
    width,
    height,
    qualityLabel: getResolutionQualityLabel(height),
  }

  if (fps !== undefined) {
    result.fps = fps
  }

  if (limitationReason !== undefined) {
    result.limitationReason = limitationReason
  }

  return result
}

export const parseInboundVideoStatsByTrackId = (
  report: RTCStatsReport,
  prevSnapshots: Map<string, RtcStatsSnapshot>,
): Map<string, InboundVideoStats> => {
  const stats = collectStats(report)
  const result = new Map<string, InboundVideoStats>()

  stats
    .filter((stat) => isVideoRtpStat(stat, 'inbound'))
    .forEach((rtpStat) => {
      const trackId = getTrackIdentifier(rtpStat)
      if (!trackId) return

      const packetsLost = typeof rtpStat.packetsLost === 'number' ? rtpStat.packetsLost : 0
      const packetsReceived = typeof rtpStat.packetsReceived === 'number' ? rtpStat.packetsReceived : 0
      const timestamp = typeof rtpStat.timestamp === 'number' ? rtpStat.timestamp : 0
      const width = typeof rtpStat.frameWidth === 'number' ? rtpStat.frameWidth : 0
      const height = typeof rtpStat.frameHeight === 'number' ? rtpStat.frameHeight : 0
      const jitterMs = typeof rtpStat.jitter === 'number' ? rtpStat.jitter * 1000 : 0
      const fps = typeof rtpStat.framesPerSecond === 'number' ? rtpStat.framesPerSecond : undefined

      const prev = prevSnapshots.get(trackId)
      const packetLossPercent = calcPacketLossPercent(packetsLost, packetsReceived, prev)
      const qualityScore = getInboundQualityScore(packetLossPercent, jitterMs)

      const inboundStats: InboundVideoStats = {
        packetLossPercent: Number(formatPacketLossPercent(packetLossPercent)),
        qualityScore,
        qualityLabel: getInboundQualityLabel(qualityScore),
        width,
        height,
      }

      if (jitterMs > 0) {
        inboundStats.jitterMs = jitterMs
      }

      if (fps !== undefined) {
        inboundStats.fps = fps
      }

      result.set(trackId, inboundStats)

      prevSnapshots.set(trackId, {
        timestamp,
        packetsLost,
        packetsReceived,
      })
    })

  return result
}

export const createOutboundSnapshot = (report: RTCStatsReport, trackId?: string): RtcStatsSnapshot | undefined => {
  const stats = collectStats(report)
  const outboundRtpStats = stats.filter((stat) => isVideoRtpStat(stat, 'outbound'))
  const rtpStat = trackId
    ? outboundRtpStats.find((stat) => getTrackIdentifier(stat) === trackId)
    : outboundRtpStats.at(0)

  if (!rtpStat) return undefined

  const bytesSent = typeof rtpStat.bytesSent === 'number' ? rtpStat.bytesSent : undefined
  const timestamp = typeof rtpStat.timestamp === 'number' ? rtpStat.timestamp : undefined

  if (typeof bytesSent !== 'number' || typeof timestamp !== 'number') return undefined

  return { timestamp, bytesSent }
}
